/**
 * Model Schema Loader Utility
 * 
 * Handles loading and validation of JSON Schema files for AI models.
 * These schemas define the input parameters required for each model.
 */

/**
 * Loads a model schema from the assets directory
 * @param {string} modelId - The model identifier (e.g., "google_imagen-4")
 * @returns {Promise<Object>} The JSON schema object
 */
export const loadModelSchema = async (modelId) => {
  try {
    // Skip special models that don't have schemas
    const specialModels = ['free', 'auto'];
    if (specialModels.includes(modelId)) {
      throw new Error(`Model "${modelId}" does not have a schema configuration`);
    }

    // Convert model ID to filename format
    // e.g., "google_imagen-4" -> "google_imagen-4.json"
    const filename = `${modelId}.json`;
    
    // Try multiple paths for compatibility
    // In production, PUBLIC_URL might be set (e.g., for subdirectory deployments)
    // In development and most Vercel deployments, it's usually empty
    const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, ''); // Remove trailing slash
    const paths = [
      // Try with basePath first (for subdirectory deployments)
      ...(basePath ? [`${basePath}/assets/models_config/${filename}`] : []),
      // Standard path (works for most deployments including Vercel)
      `/assets/models_config/${filename}`,
      // Fallback paths (commented out but kept for reference)
      // `${basePath}/models_config/${filename}`,
      // `/models_config/${filename}`
    ];
    
    let lastError = null;
    for (const url of paths) {
      try {
        // console.log(`Attempting to load schema from: ${url}`);
        const response = await fetch(url);
        
        if (response.ok) {
          // Check content type first
          const contentType = response.headers.get('content-type') || '';
          
          // If it's not JSON, check if it's HTML (likely a 404 page)
          if (!contentType.includes('application/json')) {
            // Read as text first to check if it's HTML
            const text = await response.text();
            if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html') || text.trim().startsWith('<!')) {
              console.warn(`Received HTML response from ${url} (likely 404 page), skipping...`);
              lastError = new Error(`Received HTML instead of JSON from ${url}`);
              continue;
            }
            // Try to parse as JSON anyway if it's not HTML
            try {
              const schema = JSON.parse(text);
              // console.log(`Successfully loaded schema for ${modelId} from: ${url}`);
              return schema;
            } catch (parseErr) {
              console.warn(`Failed to parse response from ${url} as JSON:`, parseErr.message);
              lastError = new Error(`Invalid JSON response from ${url}`);
              continue;
            }
          }
          
          // If content type is JSON, parse normally
          const schema = await response.json();
          // console.log(`Successfully loaded schema for ${modelId} from: ${url}`);
          return schema;
        } else {
          console.warn(`Failed to load from ${url}: Status ${response.status}`);
          lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (err) {
        // Check if error is due to JSON parsing (likely HTML response)
        if (err.message && (err.message.includes('Unexpected token') || err.message.includes('JSON'))) {
          console.warn(`Received non-JSON response from ${url} (likely HTML error page), skipping...`);
          lastError = new Error(`Invalid JSON response from ${url} - likely HTML error page`);
        } else {
          console.warn(`Error fetching from ${url}:`, err.message);
          lastError = err;
        }
      }
    }
    
    // If all paths failed, throw the last error
    throw new Error(`Failed to load schema for model: ${modelId}. Tried paths: ${paths.join(', ')}. Last error: ${lastError?.message || 'Unknown error'}`);
  } catch (error) {
    console.error(`Error loading schema for ${modelId}:`, error);
    throw error;
  }
};

/**
 * Gets all available model schemas
 * @returns {Promise<Array<string>>} Array of model IDs
 */
export const getAvailableModels = async () => {
  try {
    // Use process.env.PUBLIC_URL for proper path resolution
    const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, ''); // Remove trailing slash
    const manifestPath = basePath 
      ? `${basePath}/assets/models_config/manifest.json`
      : `/assets/models_config/manifest.json`;
    
    const response = await fetch(manifestPath);
    
    if (response.ok) {
      const manifest = await response.json();
      // console.log("manifest.models:",manifest.models)
      return manifest.models || [];
    }
    
    // Fallback: return known models
    return [
      'google_imagen-4',
      'black-forest-labs_flux-1.1-pro',
      'qwen_qwen-image-edit-plus'
    ];
  } catch (error) {
    console.error('Error loading available models:', error);
    // Return fallback list
    return [
      'google_imagen-4',
      'black-forest-labs_flux-1.1-pro',
      'qwen_qwen-image-edit-plus'
    ];
  }
};

/**
 * Validates input values against a schema
 * @param {Object} schema - The JSON schema
 * @param {Object} values - The input values to validate
 * @returns {Object} Object with isValid boolean and errors object
 */
export const validateModelInput = (schema, values) => {
  const errors = {};
  const required = schema.required || [];
  
  // Check required fields
  required.forEach(field => {
    if (values[field] === undefined || values[field] === null || values[field] === '') {
      errors[field] = `${schema.properties[field]?.title || field} is required`;
    }
  });
  
  // Validate field types and constraints
  Object.keys(schema.properties || {}).forEach(field => {
    const property = schema.properties[field];
    const value = values[field];
    
    // Skip validation if field is not provided and not required
    if (value === undefined || value === null || value === '') {
      return;
    }
    
    // Type validation
    if (property.type === 'string' && typeof value !== 'string') {
      errors[field] = `${property.title || field} must be a string`;
    } else if (property.type === 'integer' && typeof value !== 'number') {
      errors[field] = `${property.title || field} must be a number`;
    } else if (property.type === 'boolean' && typeof value !== 'boolean') {
      errors[field] = `${property.title || field} must be a boolean`;
    }
    
    // Enum validation
    if (property.enum && !property.enum.includes(value)) {
      errors[field] = `${property.title || field} must be one of: ${property.enum.join(', ')}`;
    }
    
    // Min/Max validation for numbers
    if (property.type === 'integer' || property.type === 'number') {
      if (property.minimum !== undefined && value < property.minimum) {
        errors[field] = `${property.title || field} must be at least ${property.minimum}`;
      }
      if (property.maximum !== undefined && value > property.maximum) {
        errors[field] = `${property.title || field} must be at most ${property.maximum}`;
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Gets default values from schema
 * @param {Object} schema - The JSON schema
 * @returns {Object} Object with default values
 */
export const getDefaultValues = (schema) => {
  const defaults = {};
  
  Object.keys(schema.properties || {}).forEach(field => {
    const property = schema.properties[field];
    if (property.default !== undefined) {
      defaults[field] = property.default;
    }
  });
  
  return defaults;
};

/**
 * Sorts properties by x-order for display
 * @param {Object} schema - The JSON schema
 * @returns {Array<[string, Object]>} Sorted array of [fieldName, property] tuples
 */
export const getSortedProperties = (schema) => {
  const properties = Object.entries(schema.properties || {});
  
  return properties.sort((a, b) => {
    const orderA = a[1]['x-order'] ?? 999;
    const orderB = b[1]['x-order'] ?? 999;
    return orderA - orderB;
  });
};

/**
 * Loads model specs from manifest.json and individual model JSON files
 * @returns {Promise<Array<Object>>} Array of model spec objects
 */
export const loadAllModelSpecs = async () => {
  try {
    // First, get the list of available models from manifest.json
    const modelIds = await getAvailableModels();
    // console.log('Loading specs for models:', modelIds);
    
    // Load each model's schema and extract the spec
    const modelSpecs = await Promise.all(
      modelIds.map(async (modelId) => {
        try {
          const schema = await loadModelSchema(modelId);
          
          // Extract the spec section from the schema
          if (schema && schema.spec) {
            return {
              ...schema.spec,
              // Ensure all required fields are present with fallbacks
              id: schema.spec.id || modelId,
              function: schema.spec.function || 'Image',
              name: schema.spec.name || modelId,
              vendor: schema.spec.vendor || 'Unknown',
              logo: schema.spec.logo || '',
              logoFallback: generateLogoFallback(schema.spec.function || 'Image'),
              description: schema.spec.description || 'AI model for content generation',
              cost: parsePriceToCredits(schema.spec.price || '₹0')
            };
          } else {
            console.warn(`Model ${modelId} does not have a spec section`);
            return null;
          }
        } catch (error) {
          console.error(`Failed to load spec for model ${modelId}:`, error);
          return null;
        }
      })
    );
    
    // Filter out any null values (failed loads)
    const validSpecs = modelSpecs.filter(spec => spec !== null);
    // console.log('Loaded model specs:', validSpecs);
    
    return validSpecs;
  } catch (error) {
    console.error('Error loading model specs:', error);
    return [];
  }
};

/**
 * Generates a fallback SVG logo based on model function type
 * @param {string} functionType - The model function type ('Image' or 'Video')
 * @returns {string} Data URL for SVG fallback
 */
const generateLogoFallback = (functionType) => {
  if (functionType === 'Video') {
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236366f1'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E";
  }
  // Default fallback for Image
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23888'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E";
};

/**
 * Parses price string to credits number
 * @param {string} priceString - The price string (e.g., "₹6.6/image")
 * @returns {number} The credit cost
 */
const parsePriceToCredits = (priceString) => {
  try {
    // Extract numeric value from price string
    const match = priceString.match(/[\d.]+/);
    if (match) {
      return Math.round(parseFloat(match[0]));
    }
    return 0;
  } catch (error) {
    console.error('Error parsing price:', error);
    return 0;
  }
};

/**
 * Calculates the cost of generation based on model schema and parameters
 * @param {Object} schema - The model schema
 * @param {Object} params - The current model parameters
 * @param {string} artFormat - The content type ("image" or "video")
 * @returns {number} The calculated cost in credits
 */
export const calculateModelCost = (schema, params, artFormat) => {
  if (!schema || !schema.spec) return 0;
  
  const unitCost = schema.spec.unit_cost;
  if (unitCost === undefined || unitCost === null) return 0;

  let pricing = 0;
  const modelCd = schema.spec.model_cd;

  // Handle object unit_cost (video models usually)
  if (artFormat === 'video' && typeof unitCost === 'object') {
    const vendor = schema.spec.vendor;
    let duration = 0;
    let priceFactor = '';

    // Determine duration
    if (vendor === 'Openai') {
      duration = params.seconds || 0;
    } else {
      duration = params.duration || 0;
    }

    // Determine price factor
    if (vendor?.toLowerCase() === 'google') {
      const generateAudio = params.generate_audio;
      priceFactor = generateAudio ? 'generate_audio_true' : 'generate_audio_false';
    } else if (modelCd === 'minimax/hailuo-2.3' || modelCd === 'minimax/hailuo-2.3-fast') {
      const resolution = params.resolution;
      // Format: "768pX6" or "1080pX6" etc.
      priceFactor = `${resolution}X${duration}`;
      // For this specific model, price is determined by the key (which includes duration), so duration multiplier becomes 1
      duration = 1;
    } else if (modelCd === 'kwaivgi/kling-v2.5-turbo-pro') {
      priceFactor = '720p';
    } else {
      priceFactor = params.resolution;
    }

    const factorCost = unitCost[priceFactor] || 0;
    // Based on python logic: pricing = unit_cost.get(price_factor, 0) * duration
    pricing = factorCost * duration;
  } else {
    // Image logic
    if (modelCd === 'google/nano-banana-pro') {
      const resolution = params.resolution;
      pricing = unitCost[resolution] || 0;
    } else if (typeof unitCost === 'number') {
      pricing = unitCost;
    }
  }

  // #Adding GST to the pricing
  // pricing = pricing * 1.18;
  return pricing;
};
